import Phaser from 'phaser';
import { Suit } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';
import { createSymbolGlow, SymbolGlowResult, SUIT_COLORS } from '../utils/SymbolGlow';

/**
 * Scoreboard slot index - position-based identifier for all game modes
 * Slot 0 = local player/team (bottom arc, SOUTH)
 * Slot 1 = first opponent (left arc, WEST)
 * Slot 2 = second opponent/team (top arc, NORTH)
 * Slot 3 = third opponent (right arc, EAST) - 4P FFA only
 */
export type ScoreboardSlot = 0 | 1 | 2 | 3;

/** Game score (points toward winning, 0-7 typically) */
export type GameScore = number;

/** Trick count within a hand (0-13 typically) */
export type TrickCount = number;

/** Filled dot count for trick display (0 to tricksToWin) */
type FilledDotCount = number;

/** Data stored for each scoreboard slot */
interface SlotData {
  dots: Phaser.GameObjects.Arc[];
  color: number;
  filledCount: FilledDotCount;
  arcStart: number;
  arcEnd: number;
}

/** Cached segment position info for animation targeting */
interface SegmentPosition {
  centerAngle: number;
  startAngle: number;
  endAngle: number;
  segmentWidth: number;
}

export interface PlayerInfo {
  id: string;
  name: string;
  colorNum: number;
  colorHex: string;
}

/**
 * Circular scoreboard component with:
 * - Outer ring segments for game score (7 points to win)
 * - Inner dots for trick count in current hand
 * - Trump symbol in center
 * - Support for both team mode (4P) and individual mode (2P/3P)
 */
export class Scoreboard {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;

  // Visual elements
  private outerRing!: Phaser.GameObjects.Graphics;
  private glowRing!: Phaser.GameObjects.Graphics;
  private innerDots: Phaser.GameObjects.Arc[] = []; // Legacy - kept for compatibility
  private trumpContainer!: Phaser.GameObjects.Container;
  private trumpGlow: SymbolGlowResult | null = null;
  private currentTrumpSuit: Suit | null = null;

  // Slot-based data storage (unified for all game modes)
  // Indexed by ScoreboardSlot: 0=SOUTH/local, 1=WEST, 2=NORTH, 3=EAST
  private slotData: Map<ScoreboardSlot, SlotData> = new Map();

  // Game scores per slot (points toward winning, 0-7 typically)
  private scores: GameScore[] = [0, 0, 0, 0];

  // Track current slot configuration to avoid unnecessary recreation
  private currentSlotConfig: string = '';

  // Particle emitter for celebrations
  private particleEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  // Configuration
  private readonly OUTER_RADIUS = 65;
  private readonly INNER_RADIUS = 45;
  private readonly DOT_RADIUS = 4;
  private pointsToWin = 7;
  private tricksToWin = 7;

  private playerCount = 4;

  // Cached segment positions - populated when drawing, used for animations
  // Indexed by ScoreboardSlot, value is array of segment info indexed by point number
  private segmentPositions: Map<ScoreboardSlot, SegmentPosition[]> = new Map();

  // Debug mode for testing animations (enabled via ?debugScoreboard query param)
  private debugMode = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.container = scene.add.container(x, y);
    this.container.setDepth(100);

    this.create();
    this.setupDebugMode();
  }

  private create(): void {
    this.createGlowRing();
    this.createOuterRing();
    this.createInnerDots();
    this.createTrumpIndicator();
    this.createScoreLabels();
    this.createParticleEmitter();
  }

  /**
   * Setup debug mode if ?debugScoreboard is in the URL
   * Keyboard shortcuts:
   *   1-7: Set trick count for slot 0
   *   Shift+1-7: Set trick count for slot 2
   *   Shift+Alt+1-7: Set trick count for slot 1 (3P mode)
   *   A: Animate slot 0 wins hand
   *   S: Animate slot 2 wins hand
   *   R: Reset all tricks
   */
  private setupDebugMode(): void {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('debugScoreboard')) return;

    this.debugMode = true;
    console.log('[Scoreboard Debug Mode] Enabled. Shortcuts:', {
      '1-7': 'Set tricks for slot 0',
      'Shift+1-7': 'Set tricks for slot 2',
      'Shift+Alt+1-7': 'Set tricks for slot 1 (3P mode)',
      A: 'Animate slot 0 wins hand',
      S: 'Animate slot 2 wins hand',
      R: 'Reset tricks',
    });

    this.scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this.debugMode) return;

      const slots = Array.from(this.slotData.keys());
      const hasSlot0 = slots.includes(0);
      const hasSlot1 = slots.includes(1);
      const hasSlot2 = slots.includes(2);

      // Number keys 1-7 to set trick count (use event.code for Shift compatibility)
      const digitMatch = event.code.match(/^Digit([1-7])$/);
      if (digitMatch) {
        const num = parseInt(digitMatch[1]);
        let targetSlot: ScoreboardSlot | undefined;
        if (event.shiftKey && event.altKey && hasSlot1) {
          targetSlot = 1;
        } else if (event.shiftKey && hasSlot2) {
          targetSlot = 2;
        } else if (hasSlot0) {
          targetSlot = 0;
        }
        if (targetSlot !== undefined) {
          this.setSlotTricks(targetSlot, num);
          console.log(`[Debug] Set slot ${targetSlot} tricks to ${num}`);
        }
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'a': // Animate slot 0 wins
          if (hasSlot0 && hasSlot2) {
            const score = this.scores[0] + 1;
            this.scores[0] = score;
            const loserSlots: ScoreboardSlot[] = hasSlot1 ? [1, 2] : [2];
            this.animateTricksToSlot(0, score, loserSlots);
            console.log(`[Debug] Animating slot 0 wins, score: ${score}`);
          }
          break;

        case 's': // Animate slot 2 wins
          if (hasSlot0 && hasSlot2) {
            const score = this.scores[2] + 1;
            this.scores[2] = score;
            const loserSlots: ScoreboardSlot[] = hasSlot1 ? [0, 1] : [0];
            this.animateTricksToSlot(2, score, loserSlots);
            console.log(`[Debug] Animating slot 2 wins, score: ${score}`);
          }
          break;

        case 'r': // Reset tricks
          this.resetSlotTricks();
          console.log('[Debug] Reset all tricks');
          break;
      }
    });
  }

  /**
   * Create the glow effect behind the outer ring
   */
  private createGlowRing(): void {
    this.glowRing = this.scene.add.graphics();
    this.glowRing.setAlpha(0.3);
    this.container.add(this.glowRing);
  }

  /**
   * Create the outer ring with arc segments for game score
   */
  private createOuterRing(): void {
    this.outerRing = this.scene.add.graphics();
    this.container.add(this.outerRing);
    this.drawOuterRing();
  }

  /**
   * Draw/redraw the outer ring segments based on current scores
   * Supports both team mode (4P) and individual mode (2P/3P) with per-player colors
   * Uses slot-based storage for positions and scores
   * Also populates segmentPositions cache for animation targeting
   */
  private drawOuterRing(): void {
    this.outerRing.clear();
    this.glowRing.clear();
    this.segmentPositions.clear();

    // Determine mode based on slot count: 2 slots = team/2P mode, 3+ = individual
    const isIndividualMode = this.playerCount < 4 && this.slotData.size > 2;

    if (isIndividualMode) {
      this.drawIndividualOuterRing();
    } else {
      this.drawTeamOuterRing();
    }
  }

  /**
   * Draw outer ring for team mode (4P) or 2P mode
   * Uses slotData arc positions to align with inner dots
   * Slot 0 at bottom, Slot 2 at top, dividers at left and right
   * Also caches segment positions for animation targeting
   */
  private drawTeamOuterRing(): void {
    const segmentsPerTeam = this.pointsToWin;
    const minorGap = 0.015;

    // Get active slots (typically 0 and 2 for team/2P mode)
    const activeSlots: ScoreboardSlot[] = [];
    for (const slot of this.slotData.keys()) {
      activeSlots.push(slot);
    }

    const dividerAngles: number[] = [];

    for (const slot of activeSlots) {
      const slotInfo = this.slotData.get(slot);
      if (!slotInfo) continue;

      const { arcStart, arcEnd, color } = slotInfo;
      const score = this.scores[slot];

      const arcLength = arcEnd - arcStart;
      const totalMinorGaps = (segmentsPerTeam - 1) * minorGap;
      const availableArc = arcLength - totalMinorGaps;
      const segmentAngle = availableArc / segmentsPerTeam;

      // Cache segment positions for this slot (indexed by point number)
      const slotSegments: SegmentPosition[] = [];

      // Draw segments from end to start (score fills visually left-to-right)
      for (let pointIdx = 0; pointIdx < segmentsPerTeam; pointIdx++) {
        const reverseIdx = segmentsPerTeam - 1 - pointIdx;
        const segStartAngle = arcStart + reverseIdx * (segmentAngle + minorGap);
        const segEndAngle = segStartAngle + segmentAngle;
        const centerAngle = (segStartAngle + segEndAngle) / 2;

        // Store segment position (indexed by pointIdx, so first point = index 0)
        slotSegments[pointIdx] = {
          centerAngle,
          startAngle: segStartAngle,
          endAngle: segEndAngle,
          segmentWidth: segmentAngle,
        };

        const isFilled = pointIdx < score;
        const emptyColor = PERSIAN_THEME.colors.darkOverlay;
        const fillColor = isFilled ? color : emptyColor;
        const alpha = isFilled ? 0.85 : 0.25;

        this.outerRing.lineStyle(6, fillColor, alpha);
        this.outerRing.beginPath();
        this.outerRing.arc(0, 0, this.OUTER_RADIUS, segStartAngle, segEndAngle, false);
        this.outerRing.strokePath();

        if (isFilled) {
          this.glowRing.lineStyle(10, color, 0.15);
          this.glowRing.beginPath();
          this.glowRing.arc(0, 0, this.OUTER_RADIUS, segStartAngle, segEndAngle, false);
          this.glowRing.strokePath();
        }
      }

      this.segmentPositions.set(slot, slotSegments);
      dividerAngles.push(arcEnd);
    }

    // Draw enhanced border for the whole ring
    this.drawEnhancedBorder();

    // Draw diamonds at dividers (left and right for 2-team mode)
    this.drawDiamondsAtDividers(dividerAngles);
  }

  /**
   * Draw outer ring for individual mode (3P) with per-slot colored subsegments
   * Each slot gets its own arc of subsegments around the ring, aligned with inner dots
   * Uses slotData positions for alignment
   * Also caches segment positions for animation targeting
   */
  private drawIndividualOuterRing(): void {
    if (this.slotData.size === 0) return;

    const segmentsPerPlayer = this.pointsToWin;
    const minorGap = 0.015; // Small gap between points within a player

    // Collect divider angles for diamond placement
    const dividerAngles: number[] = [];

    // Draw subsegments for each slot, using slotData positions for alignment
    for (const [slot, slotInfo] of this.slotData) {
      const { arcStart, arcEnd, color } = slotInfo;
      const score = this.scores[slot];

      const arcLength = arcEnd - arcStart;

      // Calculate segment size within this arc
      const totalMinorGaps = (segmentsPerPlayer - 1) * minorGap;
      const availableArc = arcLength - totalMinorGaps;
      const segmentAngle = availableArc / segmentsPerPlayer;

      // Cache segment positions for this slot (indexed by point number)
      const slotSegments: SegmentPosition[] = [];

      // Draw segments from end to start (so score fills visually left-to-right for bottom player)
      for (let pointIdx = 0; pointIdx < segmentsPerPlayer; pointIdx++) {
        // Reverse index so segment 0 is at the end (left side for bottom player)
        const reverseIdx = segmentsPerPlayer - 1 - pointIdx;
        const segStartAngle = arcStart + reverseIdx * (segmentAngle + minorGap);
        const segEndAngle = segStartAngle + segmentAngle;
        const centerAngle = (segStartAngle + segEndAngle) / 2;

        // Store segment position (indexed by pointIdx, so first point = index 0)
        slotSegments[pointIdx] = {
          centerAngle,
          startAngle: segStartAngle,
          endAngle: segEndAngle,
          segmentWidth: segmentAngle,
        };

        const isFilled = pointIdx < score;
        const emptyColor = PERSIAN_THEME.colors.darkOverlay;
        const fillColor = isFilled ? color : emptyColor;
        const alpha = isFilled ? 0.85 : 0.25; // Less pronounced

        // Draw segment
        this.outerRing.lineStyle(6, fillColor, alpha);
        this.outerRing.beginPath();
        this.outerRing.arc(0, 0, this.OUTER_RADIUS, segStartAngle, segEndAngle, false);
        this.outerRing.strokePath();

        // Draw subtle glow for filled segments
        if (isFilled) {
          this.glowRing.lineStyle(10, color, 0.15);
          this.glowRing.beginPath();
          this.glowRing.arc(0, 0, this.OUTER_RADIUS, segStartAngle, segEndAngle, false);
          this.glowRing.strokePath();
        }
      }

      this.segmentPositions.set(slot, slotSegments);

      // Draw major divider at the end of this slot's arc
      const markerAngle = arcEnd;
      dividerAngles.push(markerAngle);
      const markerInner = this.OUTER_RADIUS - 8;
      const markerOuter = this.OUTER_RADIUS + 8;

      // Draw major divider line
      this.outerRing.lineStyle(2, PERSIAN_THEME.colors.bronze, 0.6);
      this.outerRing.beginPath();
      this.outerRing.moveTo(Math.cos(markerAngle) * markerInner, Math.sin(markerAngle) * markerInner);
      this.outerRing.lineTo(Math.cos(markerAngle) * markerOuter, Math.sin(markerAngle) * markerOuter);
      this.outerRing.strokePath();
    }

    // Draw enhanced border with glow effect
    this.drawEnhancedBorder();

    // Draw diamonds at major divider positions
    this.drawDiamondsAtDividers(dividerAngles);
  }

  /**
   * Draw enhanced border with gradient glow and inner shadow
   */
  private drawEnhancedBorder(): void {
    // Outer glow (subtle, wide)
    this.glowRing.lineStyle(6, PERSIAN_THEME.colors.gold, 0.15);
    this.glowRing.beginPath();
    this.glowRing.arc(0, 0, this.OUTER_RADIUS + 6, 0, Math.PI * 2);
    this.glowRing.strokePath();

    // Outer border - layered for gradient effect
    this.outerRing.lineStyle(2, PERSIAN_THEME.colors.bronze, 0.4);
    this.outerRing.beginPath();
    this.outerRing.arc(0, 0, this.OUTER_RADIUS + 5, 0, Math.PI * 2);
    this.outerRing.strokePath();

    this.outerRing.lineStyle(1.5, PERSIAN_THEME.colors.gold, 0.6);
    this.outerRing.beginPath();
    this.outerRing.arc(0, 0, this.OUTER_RADIUS + 4, 0, Math.PI * 2);
    this.outerRing.strokePath();

    // Inner border with shadow effect
    this.outerRing.lineStyle(2, 0x1a1a1a, 0.5); // Dark shadow
    this.outerRing.beginPath();
    this.outerRing.arc(0, 0, this.OUTER_RADIUS - 4, 0, Math.PI * 2);
    this.outerRing.strokePath();

    this.outerRing.lineStyle(1.5, PERSIAN_THEME.colors.bronze, 0.5);
    this.outerRing.beginPath();
    this.outerRing.arc(0, 0, this.OUTER_RADIUS - 5, 0, Math.PI * 2);
    this.outerRing.strokePath();
  }

  /**
   * Draw decorative diamonds at the major divider positions
   */
  private drawDiamondsAtDividers(dividerAngles: number[]): void {
    const notchRadius = this.OUTER_RADIUS + 9; // Further out from the ring
    const diamondSize = 4; // Half-length of diamond (radial direction)
    const diamondWidth = 2.5; // Half-width of diamond (perpendicular)

    for (const angle of dividerAngles) {
      const cx = Math.cos(angle) * notchRadius;
      const cy = Math.sin(angle) * notchRadius;

      // Calculate rotated diamond points (pointing outward radially)
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);

      // Diamond points: outer tip, right side, inner tip, left side
      const outerX = cx + cosA * diamondSize;
      const outerY = cy + sinA * diamondSize;
      const innerX = cx - cosA * diamondSize;
      const innerY = cy - sinA * diamondSize;
      const rightX = cx - sinA * diamondWidth;
      const rightY = cy + cosA * diamondWidth;
      const leftX = cx + sinA * diamondWidth;
      const leftY = cy - cosA * diamondWidth;

      // Small diamond notch (subtle)
      this.outerRing.lineStyle(1, PERSIAN_THEME.colors.gold, 0.5);
      this.outerRing.beginPath();
      this.outerRing.moveTo(outerX, outerY); // Outer tip
      this.outerRing.lineTo(rightX, rightY); // Right side
      this.outerRing.lineTo(innerX, innerY); // Inner tip
      this.outerRing.lineTo(leftX, leftY); // Left side
      this.outerRing.closePath();
      this.outerRing.strokePath();
    }
  }

  /**
   * Create inner dots for trick count (configurable based on game mode)
   */
  private createInnerDots(): void {
    // Clean up existing dots
    this.innerDots.forEach((dot) => dot.destroy());
    this.innerDots = [];
    const dotCount = this.tricksToWin;

    // Arrange dots in a semicircle at the bottom of the scoreboard
    for (let i = 0; i < dotCount; i++) {
      // Spread dots across bottom semicircle
      const angle = Math.PI * 0.3 + (i / (dotCount - 1)) * Math.PI * 0.4;
      const x = Math.cos(angle) * this.INNER_RADIUS;
      const y = Math.sin(angle) * this.INNER_RADIUS;

      const dot = this.scene.add.circle(x, y, this.DOT_RADIUS, PERSIAN_THEME.colors.darkOverlay);
      dot.setStrokeStyle(1.5, PERSIAN_THEME.colors.bronze);
      dot.setAlpha(0.6);
      this.container.add(dot);
      this.innerDots.push(dot);
    }
  }

  /**
   * Create the trump suit indicator in the center
   */
  private createTrumpIndicator(): void {
    this.trumpContainer = this.scene.add.container(0, -5);
    this.container.add(this.trumpContainer);
    this.trumpContainer.setVisible(false);
  }

  /**
   * Create score labels positioned around the scoreboard
   * Note: "You/Them" labels removed per user request - scoreboard is self-explanatory
   */
  private createScoreLabels(): void {
    // Score labels are not displayed - the circular segments show the score visually
    // Keep the map for compatibility but don't create visible labels
  }

  /**
   * Create particle emitter for celebrations
   */
  private createParticleEmitter(): void {
    const key = 'gold-dust-particle';
    if (!this.scene.textures.exists(key)) return;

    this.particleEmitter = this.scene.add.particles(0, 0, key, {
      speed: { min: 30, max: 80 },
      scale: { start: 0.4 / RENDER_SCALE, end: 0.1 / RENDER_SCALE },
      alpha: { start: 0.8, end: 0 },
      lifespan: 800,
      blendMode: Phaser.BlendModes.ADD,
      tint: PERSIAN_THEME.colors.gold,
      emitting: false,
    });
    this.particleEmitter.setDepth(101);
    this.container.add(this.particleEmitter);
  }

  // =====================================================
  // SLOT-BASED API (unified for all game modes)
  // =====================================================

  /**
   * Configure slots for dot display
   * Each slot gets its own arc of dots positioned around the scoreboard
   * Slot 0 = bottom (local), 1 = left (WEST), 2 = top (NORTH), 3 = right (EAST)
   */
  setSlots(slots: { slot: ScoreboardSlot; color: number }[]): void {
    // Create a config key to detect changes
    const configKey = slots.map((s) => `${s.slot}:${s.color}:${this.tricksToWin}`).join('|');

    // Skip recreation if config hasn't changed - preserves ongoing animations
    if (configKey === this.currentSlotConfig && this.slotData.size > 0) {
      return;
    }

    this.currentSlotConfig = configKey;

    // Clear existing slot dots
    for (const slotInfo of this.slotData.values()) {
      for (const dot of slotInfo.dots) {
        dot.destroy();
      }
    }
    this.slotData.clear();

    // Hide legacy dots when using slot-based system
    for (const dot of this.innerDots) {
      dot.setVisible(false);
    }

    // Dynamically calculate arc positions based on slot count
    const n = slots.length;
    const segmentWidth = (2 * Math.PI) / n;
    const gap = 0.06;

    for (let i = 0; i < n; i++) {
      const center = Math.PI / 2 + i * segmentWidth;
      const startAngle = center - segmentWidth / 2 + gap / 2;
      const endAngle = center + segmentWidth / 2 - gap / 2;

      const slotEntry = slots[i];
      const slotDots = this.createSlotDotArc(slotEntry.color, startAngle, endAngle);

      this.slotData.set(slotEntry.slot, {
        dots: slotDots,
        color: slotEntry.color,
        filledCount: 0,
        arcStart: startAngle,
        arcEnd: endAngle,
      });
    }
  }

  /**
   * Create dot arc for a slot (internal helper for new slot-based API)
   */
  private createSlotDotArc(color: number, startAngle: number, endAngle: number): Phaser.GameObjects.Arc[] {
    const dots: Phaser.GameObjects.Arc[] = [];
    const dotCount = this.tricksToWin;

    const arcLength = endAngle - startAngle;
    const padding = arcLength * 0.08;
    const paddedStart = startAngle + padding;
    const paddedEnd = endAngle - padding;

    for (let i = 0; i < dotCount; i++) {
      const t = dotCount > 1 ? i / (dotCount - 1) : 0.5;
      const angle = paddedStart + t * (paddedEnd - paddedStart);
      const x = Math.cos(angle) * this.INNER_RADIUS;
      const y = Math.sin(angle) * this.INNER_RADIUS;

      const dot = this.scene.add.circle(x, y, this.DOT_RADIUS, PERSIAN_THEME.colors.darkOverlay);
      dot.setStrokeStyle(1.5, color);
      dot.setAlpha(0.5);
      this.container.add(dot);
      dots.push(dot);
    }

    return dots;
  }

  /**
   * Set the trick count for a specific slot (new slot-based API)
   */
  setSlotTricks(slot: ScoreboardSlot, tricks: TrickCount): void {
    const data = this.slotData.get(slot);
    if (!data) return;

    data.filledCount = tricks;

    for (let i = 0; i < data.dots.length; i++) {
      const dot = data.dots[i];
      dot.setScale(1);
      if (i < tricks) {
        dot.setFillStyle(data.color);
        dot.setAlpha(1);
      } else {
        dot.setFillStyle(PERSIAN_THEME.colors.darkOverlay);
        dot.setAlpha(0.5);
      }
    }
  }

  /**
   * Set the game score for a specific slot
   */
  setSlotScore(slot: ScoreboardSlot, score: GameScore): void {
    this.scores[slot] = score;
    this.drawOuterRing();
  }

  /**
   * Animate a specific slot's trick dot (new slot-based API)
   */
  animateSlotTrickWon(slot: ScoreboardSlot, trickIndex: number): void {
    const data = this.slotData.get(slot);
    if (!data || trickIndex < 0 || trickIndex >= data.dots.length) return;

    data.filledCount = trickIndex + 1;

    const dot = data.dots[trickIndex];
    const isLastTrick = trickIndex === this.tricksToWin - 1;
    const progress = (trickIndex + 1) / this.tricksToWin;
    this.animateDotFill(dot, data.color, isLastTrick, progress);
  }

  /**
   * Reset all slot trick dots to empty state.
   * Called after hand-end animation to prepare dots for new hand.
   * Note: Dots stay visible throughout animation (unfilled, not hidden).
   */
  resetSlotTricks(): void {
    for (const data of this.slotData.values()) {
      data.filledCount = 0;
      for (const dot of data.dots) {
        this.scene.tweens.killTweensOf(dot); // Kill any lingering tweens
        dot.setFillStyle(PERSIAN_THEME.colors.darkOverlay);
        dot.setAlpha(0.5);
        dot.setScale(1);
      }
    }
  }

  /**
   * Animate trick dots flying to outer ring for a slot.
   *
   * SEAMLESS TRANSITION: Clones are created at EXACT same position as original
   * dots BEFORE originals are unfilled. This makes it look like the same circle
   * starts moving. Order is critical: create clone FIRST, then unfill original.
   *
   * Winner clones fly to outer ring. Loser clones fly in random directions and vanish.
   * Empty dots stay visible throughout (unfilled, not hidden).
   */
  animateTricksToSlot(winnerSlot: ScoreboardSlot, winnerScore: GameScore, loserSlots: ScoreboardSlot[]): void {
    const winnerData = this.slotData.get(winnerSlot);
    if (!winnerData) return;

    // Update score and redraw outer ring
    this.scores[winnerSlot] = winnerScore;
    this.drawOuterRing();

    const winnerSegments = this.segmentPositions.get(winnerSlot);
    if (!winnerSegments) return;

    const targetSegment = winnerSegments[winnerScore - 1];
    if (!targetSegment) return;

    const { centerAngle: targetCenterAngle, segmentWidth } = targetSegment;
    const winnerFilledCount = winnerData.filledCount;
    const winnerColor = winnerData.color;

    // ========================================
    // PHASE 1: Capture positions before any changes
    // ========================================

    // Capture winner's filled dot positions
    const winnerDotPositions = winnerData.dots.slice(0, winnerFilledCount).map((dot) => ({
      x: dot.x,
      y: dot.y,
    }));

    // Capture loser's filled dot positions and colors
    const loserDotInfo: { x: number; y: number; color: number }[] = [];
    for (const loserSlot of loserSlots) {
      const loserData = this.slotData.get(loserSlot);
      if (!loserData) continue;

      for (let i = 0; i < loserData.filledCount; i++) {
        const dot = loserData.dots[i];
        loserDotInfo.push({ x: dot.x, y: dot.y, color: loserData.color });
      }
    }

    // ========================================
    // PHASE 2: Create ALL clones FIRST (for seamless transition)
    // CRITICAL: Clone must appear at EXACT same position as original
    // before we unfill the original - this makes it look like the
    // same circle starts moving.
    // ========================================

    // Create loser clones at exact positions (seamless - same position, color, size)
    const loserClones = loserDotInfo.map(({ x, y, color }) => {
      const clone = this.scene.add.circle(x, y, this.DOT_RADIUS, color);
      clone.setDepth(109);
      this.container.add(clone);
      return { clone, x, y };
    });

    // Create winner clones at exact positions (seamless - same position, color, size)
    const winnerClones = winnerDotPositions.map(({ x, y }) => {
      const clone = this.scene.add.circle(x, y, this.DOT_RADIUS, winnerColor);
      clone.setDepth(110);
      this.container.add(clone);
      return { clone, x, y };
    });

    // ========================================
    // PHASE 3: NOW unfill all slot dots (keep visible as empty circles)
    // Clones are already in place, so transition is seamless
    // ========================================

    for (const [, data] of this.slotData) {
      for (const dot of data.dots) {
        this.scene.tweens.killTweensOf(dot);
        // Unfill but keep visible - clone is already covering it
        dot.setFillStyle(PERSIAN_THEME.colors.darkOverlay);
        dot.setAlpha(0.5);
        dot.setScale(1);
      }
      data.filledCount = 0;
    }

    // ========================================
    // PHASE 4: Animate loser clones - fly in random directions and vanish
    // Player "lost" their tricks - visual of dots scattering away
    // ========================================

    loserClones.forEach(({ clone, x, y }, index) => {
      // Random direction (angle) and distance
      const randomAngle = Math.random() * Math.PI * 2;
      const randomDistance = 80 + Math.random() * 60; // 80-140 pixels
      const targetX = x + Math.cos(randomAngle) * randomDistance;
      const targetY = y + Math.sin(randomAngle) * randomDistance;

      this.scene.tweens.add({
        targets: clone,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: 0.3,
        delay: index * 80,
        duration: 1200,
        ease: 'Quad.easeOut',
        onComplete: () => clone.destroy(),
      });
    });

    // ========================================
    // PHASE 5: Animate winner clones - fly to outer ring
    // ========================================

    winnerClones.forEach(({ clone, x: startX, y: startY }, index) => {
      const spreadWidth = segmentWidth * 0.5;
      const offsetRange = winnerFilledCount > 1 ? spreadWidth : 0;
      const offsetStep = winnerFilledCount > 1 ? offsetRange / (winnerFilledCount - 1) : 0;
      const offset = -offsetRange / 2 + index * offsetStep;
      const targetAngle = targetCenterAngle + offset;

      const delay = index * 200;
      const isLast = index === winnerClones.length - 1;

      const startAngle = Math.atan2(startY, startX);
      const startRadius = Math.sqrt(startX * startX + startY * startY);
      const endAngle = targetAngle;
      const endRadius = this.OUTER_RADIUS;

      let angleDiff = endAngle - startAngle;
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
      const extraRotationPeak = Math.sign(angleDiff) * 0.4;

      this.scene.time.delayedCall(delay, () => {
        const duration = 1400;
        let elapsed = 0;
        let completed = false;

        const spiralTween = this.scene.time.addEvent({
          delay: 16,
          repeat: Math.ceil(duration / 16),
          callback: () => {
            if (completed) return;
            elapsed += 16;
            const t = Math.min(elapsed / duration, 1);
            const easeT = Phaser.Math.Easing.Cubic.Out(t);

            const radiusOvershoot = Math.sin(t * Math.PI) * 8;
            const currentRadius = startRadius + (endRadius - startRadius) * easeT + radiusOvershoot;

            const spiralT = Math.sin(t * Math.PI);
            const currentAngle = startAngle + angleDiff * easeT + extraRotationPeak * spiralT;

            const x = Math.cos(currentAngle) * currentRadius;
            const y = Math.sin(currentAngle) * currentRadius;
            clone.setPosition(x, y);

            if (t >= 1 && !completed) {
              completed = true;
              spiralTween.destroy();

              const finalX = clone.x;
              const finalY = clone.y;
              clone.destroy();

              // Particle effects
              if (this.particleEmitter) {
                const jitterX = (Math.random() - 0.5) * 6;
                const jitterY = (Math.random() - 0.5) * 6;
                this.particleEmitter.setPosition(finalX + jitterX, finalY + jitterY);

                if (isLast) {
                  const lastSparkCount = 35 + Math.floor(Math.random() * 6);
                  this.particleEmitter.setParticleTint(winnerColor);
                  this.particleEmitter.explode(lastSparkCount, 0, 0);

                  this.scene.time.delayedCall(50, () => {
                    if (this.particleEmitter) {
                      this.particleEmitter.explode(15, -5, -5);
                      this.particleEmitter.explode(15, 5, -5);
                    }
                  });

                  this.scene.time.delayedCall(80, () => {
                    if (this.particleEmitter) {
                      this.particleEmitter.setParticleTint(PERSIAN_THEME.colors.gold);
                      this.particleEmitter.explode(12, 0, 0);
                    }
                  });
                } else {
                  const sparkCount = 6 + Math.floor(Math.random() * 7);
                  this.particleEmitter.setParticleTint(winnerColor);
                  this.particleEmitter.explode(sparkCount, 0, 0);
                }
              }

              if (isLast) {
                this.pulseSlotSubsegment(winnerSlot, winnerScore - 1, winnerColor);
              }
            }
          },
        });
      });
    });
  }

  /**
   * Pulse a specific slot's subsegment (new slot-based API)
   */
  private pulseSlotSubsegment(slot: ScoreboardSlot, pointIndex: number, color: number): void {
    const slotSegments = this.segmentPositions.get(slot);
    if (!slotSegments) return;

    const segment = slotSegments[pointIndex];
    if (!segment) return;

    const { startAngle, endAngle } = segment;

    const pulseArc = this.scene.add.graphics();
    pulseArc.lineStyle(10, color, 0.8);
    pulseArc.beginPath();
    pulseArc.arc(0, 0, this.OUTER_RADIUS + 3, startAngle, endAngle, false);
    pulseArc.strokePath();
    this.container.add(pulseArc);

    this.scene.tweens.add({
      targets: pulseArc,
      alpha: 1.0,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 150,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: pulseArc,
          scaleX: 1.15,
          scaleY: 1.15,
          duration: 200,
          ease: 'Quad.easeOut',
          onComplete: () => {
            this.scene.tweens.add({
              targets: pulseArc,
              scaleX: 1.0,
              scaleY: 1.0,
              duration: 150,
              ease: 'Quad.easeInOut',
              onComplete: () => {
                this.scene.tweens.add({
                  targets: pulseArc,
                  alpha: 0,
                  duration: 200,
                  ease: 'Quad.easeOut',
                  onComplete: () => pulseArc.destroy(),
                });
              },
            });
          },
        });
      },
    });
  }

  /**
   * Animate when a game point is scored for a slot (new slot-based API)
   */
  animateSlotPointScored(slot: ScoreboardSlot, _newScore: GameScore): void {
    const data = this.slotData.get(slot);
    const color = data?.color ?? PERSIAN_THEME.colors.gold;

    // Flash the outer ring
    const flash = this.scene.add.graphics();
    flash.lineStyle(12, color, 0.8);
    flash.beginPath();
    flash.arc(0, 0, this.OUTER_RADIUS, 0, Math.PI * 2);
    flash.strokePath();
    this.container.add(flash);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 500,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Particle burst at the slot's arc position
    if (this.particleEmitter && data) {
      const arcCenter = (data.arcStart + data.arcEnd) / 2;
      const x = Math.cos(arcCenter) * this.OUTER_RADIUS;
      const y = Math.sin(arcCenter) * this.OUTER_RADIUS;
      this.particleEmitter.setPosition(x, y);
      this.particleEmitter.setParticleTint(color);
      this.particleEmitter.explode(20, 0, 0);
    }
  }

  /**
   * Set the trump suit and animate its appearance
   */
  setTrumpSuit(suit: Suit | null, animate: boolean = true): void {
    if (suit === null) {
      this.trumpContainer.setVisible(false);
      this.currentTrumpSuit = null;
      return;
    }

    // Only recreate if suit changed
    if (suit !== this.currentTrumpSuit) {
      // Remove old glow
      if (this.trumpGlow) {
        this.trumpGlow.container.destroy();
        this.trumpGlow = null;
      }

      // Create new symbol glow using shared utility
      const colors = SUIT_COLORS[suit];
      this.trumpGlow = createSymbolGlow(this.scene, suit, {
        symbolSize: 65,
        mainColor: colors.main,
        glowColor: colors.glow,
        strokeThickness: 1.5,
        shadowOffset: 2,
        glowSizeMultipliers: [2.0, 1.7, 1.4],
        glowAlphas: [0.05, 0.08, 0.12],
      });

      this.trumpContainer.add(this.trumpGlow.container);
      this.currentTrumpSuit = suit;
    }

    const isRed = suit === Suit.HEARTS || suit === Suit.DIAMONDS;

    if (animate) {
      // Animate trump appearing
      this.trumpContainer.setScale(0);
      this.trumpContainer.setAlpha(0);
      this.trumpContainer.setVisible(true);

      this.scene.tweens.add({
        targets: this.trumpContainer,
        scale: 1,
        alpha: 1,
        duration: 400,
        ease: 'Back.easeOut',
      });

      // Particle burst with suit-colored sparks
      if (this.particleEmitter) {
        // Hearts/Diamonds: Red/pink sparks, Spades/Clubs: White/silver sparks
        const sparkColor = isRed ? 0xff4444 : 0xffffff;
        const sparkColorAlt = isRed ? 0xff6666 : 0xcccccc;

        // Set emitter to trump container position - it may be left elsewhere from previous animation
        this.particleEmitter.setPosition(this.trumpContainer.x, this.trumpContainer.y);
        this.particleEmitter.setParticleTint(sparkColor);
        this.particleEmitter.explode(10, 0, 0);
        this.particleEmitter.setParticleTint(sparkColorAlt);
        this.particleEmitter.explode(8, -5, -2);
        this.particleEmitter.explode(8, 5, -2);
      }
    } else {
      this.trumpContainer.setScale(1);
      this.trumpContainer.setAlpha(1);
      this.trumpContainer.setVisible(true);
    }
  }

  /**
   * Animate a dot being filled - fade in then spark
   * Enhanced with progressive effects based on game progress
   */
  private animateDotFill(
    dot: Phaser.GameObjects.Arc,
    color: number = PERSIAN_THEME.colors.gold,
    isLastTrick: boolean = false,
    progress: number = 0.5
  ): void {
    // Set the fill color for the dot
    dot.setFillStyle(color);

    // Start invisible and small
    dot.setAlpha(0);
    dot.setScale(0.5);

    // Pulse scale increases with progress (more dramatic as game nears end)
    const baseScale = 1.5 + progress * 0.5; // 1.5 to 2.0
    const pulseScale = isLastTrick ? 2.5 : baseScale;
    const pulseDuration = isLastTrick ? 250 : 180;

    // Particle count scales with progress
    const baseParticles = 8 + Math.floor(progress * 12); // 8 to 20
    const particleCount = isLastTrick ? 30 : baseParticles;

    // Fade in and grow the dot
    this.scene.tweens.add({
      targets: dot,
      alpha: 1,
      scale: 1,
      duration: 400,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        // After fade in, pulse animation
        this.scene.tweens.add({
          targets: dot,
          scale: pulseScale,
          duration: pulseDuration,
          yoyo: true,
          ease: isLastTrick ? 'Back.easeOut' : 'Quad.easeOut',
        });

        // Particle burst at dot position with player color
        if (this.particleEmitter) {
          this.particleEmitter.setPosition(dot.x, dot.y);
          this.particleEmitter.setParticleTint(color);
          this.particleEmitter.explode(particleCount, 0, 0);

          // Extra burst for last trick
          if (isLastTrick) {
            this.scene.time.delayedCall(100, () => {
              this.particleEmitter?.explode(15, -10, -10);
              this.particleEmitter?.explode(15, 10, -10);
            });
          }
        }
      },
    });
  }

  /**
   * Celebrate special scoring (Kot, Hakem Koti) with extra particle effects
   */
  celebrateSpecialScore(): void {
    // Big particle burst in multiple directions
    if (this.particleEmitter) {
      this.particleEmitter.explode(40, 0, 0);
      this.scene.time.delayedCall(100, () => {
        this.particleEmitter?.explode(25, -30, -20);
        this.particleEmitter?.explode(25, 30, -20);
      });
    }

    // Flash the entire scoreboard with gold
    const flash = this.scene.add.graphics();
    flash.fillStyle(PERSIAN_THEME.colors.gold, 0.3);
    flash.fillCircle(0, 0, this.OUTER_RADIUS + 10);
    this.container.add(flash);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 1.3,
      duration: 600,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Shake the scoreboard container
    const originalX = this.container.x;
    this.scene.tweens.add({
      targets: this.container,
      x: originalX + 5,
      duration: 50,
      yoyo: true,
      repeat: 5,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Reset for new hand
   */
  resetHand(): void {
    this.resetSlotTricks();
  }

  /**
   * Reset everything for new game
   */
  resetGame(): void {
    // Reset slot-based storage
    this.scores = [0, 0, 0, 0];
    this.currentSlotConfig = '';

    this.drawOuterRing();
    this.resetSlotTricks();
    this.trumpContainer.setVisible(false);
  }

  /**
   * Configure for team mode (4P) or individual mode (2P/3P)
   * Note: Visual display is the same for both modes (circular segments)
   */
  setMode(_isTeamMode: boolean, _players?: PlayerInfo[]): void {
    // No special handling needed - scoreboard visuals are the same
  }

  /**
   * Configure game settings for different game modes.
   * @param pointsToWin - Points needed to win (3 or 7, default 7)
   * @param playerCount - Number of players (2, 3, or 4) to determine tricks needed
   */
  setGameConfig(pointsToWin: 3 | 7 = 7, playerCount: 2 | 3 | 4 = 4): void {
    const prevPointsToWin = this.pointsToWin;
    const prevTricksToWin = this.tricksToWin;
    const prevPlayerCount = this.playerCount;

    this.pointsToWin = pointsToWin;
    this.playerCount = playerCount;

    // 7 tricks to win in all modes (official rules)
    this.tricksToWin = 7;

    // Redraw if changed
    if (prevPointsToWin !== this.pointsToWin || prevPlayerCount !== this.playerCount) {
      this.drawOuterRing();
    }
    if (prevTricksToWin !== this.tricksToWin) {
      this.createInnerDots();
    }
  }

  /**
   * Set visibility of the scoreboard
   */
  setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Get the container for positioning
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Update loop for any animations (call from scene update)
   */
  update(time: number): void {
    // Subtle glow pulse on trump symbol
    if (this.trumpContainer.visible && this.trumpGlow) {
      const pulse = 1 + Math.sin(time / 600) * 0.3;
      this.trumpGlow.setGlowIntensity(pulse);
    }
  }

  /**
   * Set the position of the scoreboard
   */
  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Clean up all elements
   */
  destroy(): void {
    this.container.destroy();
  }
}
